<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useEscapeKey } from '../../composables/useEscapeKey';
import { setScrollLock } from '../../composables/useScrollLock';

defineProps<{
  darkMode?: boolean;
  title: string;
}>();

const emit = defineEmits<{ (event: 'close'): void }>();

const transitionDuration = 300;
const animatingIn = ref(false);

onMounted(() => {
  // add the transition class after the first render to trigger the transition
  requestAnimationFrame(() => (animatingIn.value = true));
  // set scroll lock after the transition is done and the panel is visible
  setTimeout(() => setScrollLock(true), transitionDuration);
});

function closePanel() {
  // remove scroll lock before the transition is started
  setScrollLock(false);
  // remove the transition class to trigger the transition
  requestAnimationFrame(() => (animatingIn.value = false));
  // emit the close panel after the transition is done
  setTimeout(() => emit('close'), transitionDuration);
}

useEscapeKey(closePanel);
</script>

<template>
  <Teleport to="body">
    <section
      :class="['owpi-second-level', { 'owpi-second-level--theme-dark': darkMode, 'animate-in': animatingIn }]"
      role="dialog"
      aria-modal="true"
      :style="{ '--_duration': `${transitionDuration}ms` }"
    >
      <div class="background" @click="closePanel"></div>
      <div class="panel">
        <div class="top-bar">
          <p class="label wb-type-copy-strong">{{ title }}</p>
          <button ref="closeBtn" class="close-btn wb-round-button wb-round-button--small" @click="closePanel">
            <wb-icon name="bds/close/24"></wb-icon>
          </button>
        </div>
        <slot></slot>
      </div>
    </section>
  </Teleport>
</template>

<style lang="scss" scoped>
@import 'variables.scss';
.owpi-second-level {
  position: fixed;
  inset: 0;
  z-index: 1000;

  .background {
    position: absolute;
    inset: 0;
    background: var(--wb-black);
    cursor: pointer;
    opacity: 0;
    transition: opacity var(--_duration) var(--wb-fade);
  }

  &.animate-in {
    .background {
      opacity: 0.8;
    }
    .panel {
      transform: translateX(0);
    }
  }

  .panel {
    position: absolute;
    inset-block: 0;
    inset-inline-end: 0;
    background: white;

    --hide-percentage: 100%;

    @at-root [dir='rtl'] & {
      --hide-percentage: -100%;
    }

    .top-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 12px 14px var(--wb-spacing-s);
      background-color: var(--wb-white);
      border-bottom: 1px solid var(--wb-grey-85);

      position: sticky;
      top: 0;
      z-index: 10;
    }

    transform: translateX(var(--hide-percentage));
    transition: transform var(--_duration) var(--wb-fade);
    width: 100%;
    max-width: $second-level-width;
    overflow-y: auto;
  }

  .close-btn {
    z-index: 34;

    color: var(--wb-grey-20);

    wb-icon {
      pointer-events: none;
    }
  }

  &--theme-dark {
    @extend %theme-amg;
  }
}
</style>
